import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import dayjs from 'dayjs';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import { ProductMeasurement } from '@interfaces/models/productMeasurement';
import { ProductPackaging } from '@interfaces/models/productPackaging';
import { isVintage } from '@helpers/utils/products';
import { ModalContext } from '@context/modal.context';
import { PurchaseOrigin } from '@interfaces/models/productPurchaseOrigin';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useProductQuery from '@hooks/product/use-product-query';
import dynamic from 'next/dynamic';
import styles from './product-description-list.module.scss';

const SizeGuideWrapper = dynamic(() => import('@components/atoms/size-guide-wrapper/size-guide-wrapper'), {
  ssr: false,
});

const ConditionGuideModal = dynamic(
  () => import('@components/molecules/products/product-description/condition-guide-modal/condition-guide-modal'),
  {
    ssr: false,
  },
);

interface Properties {
  details: Detail[];
  measurements: ProductMeasurement[];
  packaging: ProductPackaging[];
}

interface Detail {
  /**
   * Property name that's shown on the left side of each list item
   */
  property: string;
  /**
   * Property value that's shown on the right side of each list item
   */
  value: string;
  /**
   * Condition that must be true for that list item to be shown
   */
  showIf: boolean;
  /**
   * Any additional HTML that needs to be rendered with your list item
   */
  additionalHTML?: React.JSX.Element;
}

const ProductDescriptionList: React.FC = () => {
  const { t } = useTranslation();
  const { productShipping } = useProductShippingQuery();
  const { product } = useProductQuery();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  const [hiddenSmDList, setHiddenSmDList] = useState<boolean>(true);

  const openConditionGuideModal = () => openModal(<ConditionGuideModal productId={product.id} />);

  const properties = useMemo<Properties>(
    () => ({
      details: [
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.ON_LINE_SINCE'),
          value: dayjs(product.creationDate).format('YYYY-MM-DD'),
          showIf: true,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.UNIVERSE'),
          value: product.universe.localizedName,
          showIf: !!product.universe?.localizedName,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.CATEGORY'),
          value: product?.category?.parent?.localizedName,
          showIf: !!product.category?.parent?.localizedName,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.SUBCATEGORY'),
          value: product?.category?.localizedName,
          showIf: !!product?.category?.localizedName,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.BRAND'),
          value: product?.brand?.name,
          showIf: !!product?.brand?.name,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.MODEL'),
          value: product?.model?.localizedName,
          showIf: !!product?.model?.localizedName,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.CONDITION'),
          value: product?.condition?.description,
          showIf: !!product?.condition?.description,
          additionalHTML: (
            <Button
              data-cy="open-condition-guide-modal"
              disableDefaultStyling
              className={clsx(styles.descriptionList__button, 'link', 'vc-text-m')}
              onClick={openConditionGuideModal}
            >
              {t('PRODUCT.DETAILS.MORE_INFO')}
            </Button>
          ),
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.MATERIAL_BOX'),
          value: product?.material?.parent?.localizedName ?? product?.material?.localizedName,
          showIf: !!product?.watchStrapMaterial,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.MATERIAL'),
          value: product?.material?.parent?.localizedName ?? product?.material?.localizedName,
          showIf: !product?.watchStrapMaterial,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.MATERIAL_WATCHSTRAP'),
          value: product?.watchStrapMaterial?.localizedName,
          showIf: !!product?.watchStrapMaterial,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.MECHANISM'),
          value: product?.watchMechanism?.localizedName,
          showIf: !!product?.watchMechanism,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.COLOR'),
          value: product?.color?.localizedName,
          showIf: !!product?.color,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.PATTERN'),
          value: product?.pattern?.localizedName,
          showIf: !!product?.pattern,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.SIZE'),
          // Note: product?.size?.localizedStandard seems to be empty on some products, so it looks clunky
          value: `${product?.size?.size} ${product?.size?.localizedStandard}`,
          showIf: !!product?.size?.size && !!product?.size?.standard,
          additionalHTML: (
            <SizeGuideWrapper
              universeId={product.universe.id}
              productCategoryId={product.category.id}
              sizeGuideStandard={product?.size?.standard}
              sizeGuideSize={product?.size?.size}
              className={clsx(styles.descriptionList__button, 'link', 'vc-text-m')}
            >
              {t('PRODUCT.DETAILS.SIZES_GUIDE')}
            </SizeGuideWrapper>
          ),
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.STYLE'),
          value: t('PRODUCT.DESCRIPTION.DETAILS.VINTAGE'),
          showIf: isVintage(product),
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.LOCALISATION'),
          value:
            productShipping && product?.inStock
              ? t('PRODUCT.DESCRIPTION.DETAILS.PRODUCT_LOCALISATION_VC', {
                  shipping: productShipping,
                })
              : t('PRODUCT.DETAILS.PRODUCT_LOCALISATION', {
                  product,
                }),
          showIf: true,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.ORIGIN'),
          value: t('PRODUCT.DESCRIPTION.DETAILS.ORIGIN_PRIVATE_SALE'),
          showIf: product.purchaseOrigin === PurchaseOrigin.PRIVATE_SALE,
        },
        {
          property: t('PRODUCT.DESCRIPTION.DETAILS.REFERENCE'),
          value: product.id,
          showIf: true,
        },
      ],
      measurements: product.measurements ?? [],
      packaging: product.packagings ?? [],
    }),
    [product, productShipping],
  );

  return (
    <>
      <div className={clsx(styles.descriptionList, 'd-md-flex', hiddenSmDList && 'd-none')}>
        {/* Left column */}
        <div className={clsx(styles.descriptionList__column, styles['descriptionList__column--left'])}>
          {/* Detail */}
          <div className={styles.descriptionList__block}>
            <h3 className={clsx(styles.descriptionList__title, 'vc-text-l')}>
              {t('PRODUCT.DESCRIPTION.DETAILS.TITLE_INFOS')}
            </h3>

            <ul className={styles.descriptionList__list}>
              {properties.details
                .filter((detail: Detail) => detail.showIf)
                .map((detail: Detail, index: number) => (
                  <li
                    className={styles.descriptionList__listItem}
                    key={`${detail.property}-${detail.value}`}
                    suppressHydrationWarning={index === 0} // Suppress hydration warning for first item: creationDate
                  >
                    <span className={styles.descriptionList__property}>{detail.property}</span>

                    <span className={styles.descriptionList__value}>{detail.value}</span>

                    {detail?.additionalHTML && <>{detail.additionalHTML}</>}
                  </li>
                ))}
            </ul>
          </div>
        </div>

        {/* Right column show only show when either measurements or packaging exists */}
        {(properties.measurements.length > 0 || properties.packaging.length > 0) && (
          <div className={clsx(styles.descriptionList__column, styles['descriptionList__column--right'])}>
            {/* Measurements */}
            {properties.measurements.length > 0 && (
              <div className={styles.descriptionList__block}>
                <h3 className={clsx(styles.descriptionList__title, 'vc-text-l')}>
                  {t('PRODUCT.DESCRIPTION.DETAILS.TITLE_DIMENSIONS')}
                </h3>

                <ul className={styles.descriptionList__list}>
                  {properties.measurements.map((measurement: ProductMeasurement) => (
                    <li
                      className={styles.descriptionList__listItem}
                      key={measurement.name}
                    >
                      <span className={styles.descriptionList__property}>{measurement.localizedName}:</span>

                      <span className={styles.descriptionList__value}>
                        {measurement.value} {product.type === 'product' && product.unit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Packaging */}
            {properties.packaging.length > 0 && (
              <div className={styles.descriptionList__block}>
                <h3 className={clsx(styles.descriptionList__title, 'vc-text-l')}>
                  {t('PRODUCT.DESCRIPTION.DETAILS.TITLE_PACKAGING')}
                </h3>

                <ul className={styles.descriptionList__list}>
                  {properties.packaging.map((packaging: ProductPackaging) => (
                    <li
                      className={styles.descriptionList__listItem}
                      key={packaging.name}
                    >
                      <span className={styles.descriptionList__property}>{packaging.localizedName}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Show more / less mobile button */}
      <Button
        type="submit"
        variant={hiddenSmDList ? 'secondary' : 'primary'}
        size="medium"
        fullWidth
        className={clsx(styles.descriptionList__btnDisplay, 'd-md-none')}
        onClick={() => setHiddenSmDList((prevValue) => !prevValue)}
        data-cy="product-description-toggle-description-length-button"
      >
        <span className={clsx(!hiddenSmDList && 'd-none')}>{t('PRODUCT.DESCRIPTION.DETAILS.SHOW_DETAILS_BTN')}</span>

        <span className={clsx(hiddenSmDList && 'd-none')}>{t('PRODUCT.DESCRIPTION.DETAILS.HIDE_DETAILS_BTN')}</span>
      </Button>
    </>
  );
};

export default React.memo(ProductDescriptionList);
